import type { Address } from "viem"
import type { Config } from "wagmi"
import { formatUnits } from "viem"

import type {
  ApprovalMode,
  EmitEvent,
  TokenAddress,
  TokenDecimals,
  TokenSymbol,
  WeiValue,
} from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { EventEmitter } from "@/domains/library"
import type { TransactionReturnType } from "@/domains/on-chain"
import { weiValueToNumber } from "@/domains/core"
import { approveOwnerSpenderAllowance } from "@/domains/on-chain"
import { ManualClLiquidityIncreasedEvent } from "@/domains/thick-v2/events"

import { increaseThickNfpPosition } from "@/domains/on-chain/transactions/thick-v2/increase-thick-nfp-position"

/**
 * Lens shape this interaction needs: current wallet balance + current NFPM
 * allowance for both pool tokens. This mirrors the one hook-sourced shape we
 * currently have; once an `AllowanceRepository` lands, the approval logic will
 * move out of here and into a dedicated guard.
 *
 * @todo MIGRATE: move approval logic to AllowanceRepository + approval guards.
 */
export type IncreaseNfpPositionLensResult = {
  token0: { balance: WeiValue; allowance: WeiValue }
  token1: { balance: WeiValue; allowance: WeiValue }
}

/**
 * Increase-liquidity interaction for a thick-v2 position.
 *
 * Performs balance and allowance guards (approving the NFPM for each token as
 * needed), submits the `increaseLiquidity` tx, and publishes
 * `ManualClLiquidityIncreasedEvent` on success so the position orchestrator
 * re-hydrates.
 */
export interface IncreaseThickNfpInteractionParams {
  account: Address
  chainId: number
  config: Config
  tokenId: bigint
  poolAddress: Address
  token0: TokenAddress & TokenDecimals & TokenSymbol
  token1: TokenAddress & TokenDecimals & TokenSymbol
  amount0Desired: bigint
  amount1Desired: bigint
  amount0Min: bigint
  amount1Min: bigint
  deadline: number
  nfpManagerAddress: Address
  emitter: EventEmitter<EmitEvent>
  lens: IncreaseNfpPositionLensResult
  approvalMode: ApprovalMode
  eventBus: IEventBus
  onIncreaseThickNfpBegin?: () => void
}

export const increaseThickNfpInteraction = async ({
  account,
  chainId,
  config,
  tokenId,
  poolAddress,
  token0,
  token1,
  amount0Desired,
  amount1Desired,
  amount0Min,
  amount1Min,
  nfpManagerAddress,
  deadline,
  emitter,
  lens,
  approvalMode,
  eventBus,
  onIncreaseThickNfpBegin,
}: IncreaseThickNfpInteractionParams): Promise<TransactionReturnType | undefined> => {
  if (lens.token0.balance.wei < amount0Desired) {
    const balance = weiValueToNumber(lens.token0.balance)
    emitter.emit("increase-thick-nfp", {
      title: "Insufficient balance",
      description: `You have ${balance} ${token0.symbol} but you are trying to spend ${formatUnits(amount0Desired, token0.decimals)} ${token0.symbol}`,
      variant: "error",
    })
    return undefined
  }

  if (lens.token1.balance.wei < amount1Desired) {
    const balance = weiValueToNumber(lens.token1.balance)
    emitter.emit("increase-thick-nfp", {
      title: "Insufficient balance",
      description: `You have ${balance} ${token1.symbol} but you are trying to spend ${formatUnits(amount1Desired, token1.decimals)} ${token1.symbol}`,
      variant: "error",
    })
    return undefined
  }

  if (lens.token0.allowance.wei < amount0Desired) {
    emitter.emit("approve-token-allowance", {
      title: "Approving spending tokens",
      description: `Seeking approval of ${formatUnits(amount0Desired, token0.decimals)} ${token0.symbol}`,
      variant: "info",
    })
    const approval = await approveOwnerSpenderAllowance({
      account,
      chainId,
      config,
      contractAddress: token0.address,
      spenderAddress: nfpManagerAddress,
      spenderAmount: amount0Desired,
      approvalMode,
      contractRiskLevel: "immutable",
    })
    if (approval.success) {
      emitter.emit("approve-token-allowance", { title: "Approval successful", variant: "success" })
    } else {
      emitter.emit("approve-token-allowance", {
        title: "Unable to approve spending tokens",
        description: approval.error,
        variant: "error",
      })
      return undefined
    }
  }

  if (lens.token1.allowance.wei < amount1Desired) {
    emitter.emit("approve-token-allowance", {
      title: "Approving spending tokens",
      description: `Seeking approval of ${formatUnits(amount1Desired, token1.decimals)} ${token1.symbol}`,
      variant: "info",
    })
    const approval = await approveOwnerSpenderAllowance({
      account,
      chainId,
      config,
      contractAddress: token1.address,
      spenderAddress: nfpManagerAddress,
      spenderAmount: amount1Desired,
      approvalMode,
      contractRiskLevel: "immutable",
    })
    if (approval.success) {
      emitter.emit("approve-token-allowance", { title: "Approval successful", variant: "success" })
    } else {
      emitter.emit("approve-token-allowance", {
        title: "Unable to approve spending tokens",
        description: approval.error,
        variant: "error",
      })
      return undefined
    }
  }

  onIncreaseThickNfpBegin?.()

  const result = await increaseThickNfpPosition({
    account,
    chainId,
    config,
    tokenId,
    amount0Desired,
    amount1Desired,
    amount0Min,
    amount1Min,
    deadline,
    nfpManagerAddress,
  })

  if (result.success) {
    emitter.emit("increase-thick-nfp", { title: "Increased position", variant: "success" })
    eventBus.publish(
      new ManualClLiquidityIncreasedEvent({
        poolAddress,
        tokenId,
        transactionHash: "tx" in result ? result.tx : undefined,
      }),
    )
    return result
  }

  emitter.emit("increase-thick-nfp", {
    title: "Unable to increase position",
    description: "error" in result ? result.error : undefined,
    variant: "error",
  })

  return result
}
